
/* ==========================================================================
   ACCESSIBLE LAYER / MODAL FOCUS MANAGEMENT (/database/a11y.js)
   ========================================================================== */

const layerState = new WeakMap();
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function visibleFocusable(layer) {
  return [...layer.querySelectorAll(FOCUSABLE)].filter(el => {
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none' && !el.hasAttribute('hidden');
  });
}

export function activateAccessibleLayer(layer, options = {}) {
  if (!layer) return;
  const existingState = layerState.get(layer);
  const previousFocus = existingState?.previousFocus || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  deactivateAccessibleLayer(layer, { restoreFocus: false });
  const close = typeof options.onClose === 'function' ? options.onClose : null;
  const keyHandler = (event) => {
    if (event.key === 'Escape' && close) {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = visibleFocusable(layer);
    if (!focusable.length) {
      event.preventDefault();
      layer.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  layer.setAttribute('role', options.role || 'dialog');
  layer.setAttribute('aria-modal', 'true');
  layer.setAttribute('aria-hidden', 'false');
  if (!layer.hasAttribute('tabindex')) layer.setAttribute('tabindex', '-1');
  if (options.label && !layer.getAttribute('aria-label') && !layer.getAttribute('aria-labelledby')) {
    layer.setAttribute('aria-label', options.label);
  }

  document.addEventListener('keydown', keyHandler, true);
  layerState.set(layer, { previousFocus, keyHandler });

  queueMicrotask(() => {
    const target = options.initialFocusSelector ? layer.querySelector(options.initialFocusSelector) : null;
    const fallback = visibleFocusable(layer)[0] || layer;
    (target || fallback)?.focus?.();
  });
}

export function deactivateAccessibleLayer(layer, options = {}) {
  if (!layer) return;
  const current = layerState.get(layer);
  if (current?.keyHandler) document.removeEventListener('keydown', current.keyHandler, true);
  layerState.delete(layer);
  layer.setAttribute('aria-hidden', 'true');

  if (options.restoreFocus !== false && current?.previousFocus?.isConnected) {
    queueMicrotask(() => current.previousFocus.focus());
  }
}
